/**
 * Inline change history.
 *
 * BFH VI B 37/23 requires the history to be apparent "bei gewöhnlicher
 * Einsichtnahme in die Datei" — so it renders here, expanded, next to the
 * record itself. It is not behind a tab, a toggle, or an admin screen.
 */

import { useEffect, useState } from 'react';
import { auditForEntity } from '@/db/audit';
import { formatDateTime } from '@/domain/dates';
import type { AuditAction, AuditEntry, FieldChange } from '@/domain/types';
import { useSession } from '@/session/SessionContext';
import { useT, type Dictionary } from '@/i18n';
import { Badge, type ToneName } from './ui';

const ACTION_TONES: Partial<Record<AuditAction, ToneName>> = {
  create: 'success',
  update: 'warning',
  delete: 'danger',
  lock: 'info',
  'invoice.cancel': 'danger',
};

/**
 * Translation happens at display time, never on the stored entry.
 *
 * The German label and the `ja`/`nein` values are inside the SHA-256 preimage
 * of the entry, so rewriting them would break the chain. The stored `field`
 * key is looked up in the current dictionary instead, and the stored label is
 * the fallback for keys the dictionary does not know — an entry written by an
 * older version stays readable.
 */
function fieldLabel(change: FieldChange, t: Dictionary): string {
  return t.audit.fields[change.field] ?? change.label;
}

function fieldValue(value: string, t: Dictionary): string {
  if (value === 'ja') return t.audit.booleanTrue;
  if (value === 'nein') return t.audit.booleanFalse;
  return value;
}

export function AuditTrail({ entityId }: { entityId: string }) {
  const session = useSession();
  const t = useT();
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);

  useEffect(() => {
    void (async () => setEntries(await auditForEntity(session.dataKey, entityId)))();
  }, [session.dataKey, entityId]);

  if (!entries) return <p className="text-muted text-sm">{t.audit.loading}</p>;
  if (entries.length === 0) return <p className="text-muted text-sm">{t.audit.empty}</p>;

  return (
    <ol className="space-y-3">
      {entries.map((entry) => (
        <li key={entry.hash} className="border-line-strong border-l-2 pl-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={ACTION_TONES[entry.action] ?? 'neutral'}>
              {t.audit.actions[entry.action] ?? entry.action}
            </Badge>
            <span className="text-muted text-xs">{formatDateTime(entry.at)}</span>
            <span className="text-muted font-mono text-[10px]">#{entry.seq}</span>
          </div>

          {entry.reason && (
            <p className="mt-1.5 text-xs text-amber-700">
              {t.audit.reason} {entry.reason}
            </p>
          )}

          {entry.changes.length > 0 && (
            <ul className="mt-2 space-y-1">
              {entry.changes.map((change) => (
                <li key={change.field} className="text-xs">
                  <span className="text-muted">{fieldLabel(change, t)}: </span>
                  {change.before !== null && (
                    <span className="text-muted line-through">
                      {fieldValue(change.before, t)}
                    </span>
                  )}
                  {change.before !== null && change.after !== null && (
                    <span className="text-muted"> → </span>
                  )}
                  {change.after !== null && (
                    <span className="text-heading">{fieldValue(change.after, t)}</span>
                  )}
                </li>
              ))}
            </ul>
          )}

          <p className="text-muted/70 mt-1.5 font-mono text-[10px] break-all">
            {entry.hash.slice(0, 32)}…
          </p>
        </li>
      ))}
    </ol>
  );
}
